// Lógica pura de la sección Árboles: riesgo, estado de la evaluación,
// filtros, menciones de la inspección diaria e historial de la ficha. Sin red
// ni React. La pantalla, el estado y el reporte consumen esto.
//
// Tablas (migración 20261014100000_cables_arboles): cables_arboles (ficha),
// cables_arbol_evaluaciones (append-only, se anulan con motivo),
// cables_arboles_historial (fila anterior en jsonb) y la vista
// cables_v_arboles_estado (árbol + última evaluación vigente + riesgo +
// proxima_evaluacion + vencida + sin_evaluacion + menciones de 30 días).
import { normalizarTexto } from './auditoria';
import { instalacionDe, instalacionValida, etiquetaInstalacion } from './auditoriaInstalaciones';

export const RIESGOS = ['bajo', 'medio', 'alto', 'extremo'];
export const RIESGO_LABEL = { bajo: 'Bajo', medio: 'Medio', alto: 'Alto', extremo: 'Extremo' };
export const RIESGO_ORDEN = { bajo: 1, medio: 2, alto: 3, extremo: 4 };

// Colores con los tokens de estado de VLS (vls-tokens.css). Extremo: fondo
// lleno de error y borde doble, con el texto "Extremo" (nunca solo color).
export const RIESGO_PILL = {
  bajo: 'border bg-success/10 text-success border-success/30',
  medio: 'border bg-warning/15 text-warning-foreground border-warning/50',
  alto: 'border bg-error/10 text-error border-error/40',
  extremo: 'border-2 bg-error text-error-foreground border-error',
};

// Realce lateral de una fila o tarjeta según el riesgo.
export const RIESGO_FILA = {
  bajo: '',
  medio: 'border-l-4 border-l-warning',
  alto: 'border-l-4 border-l-error bg-error/5',
  extremo: 'border-l-8 border-l-error bg-error/10',
};

// Para el PDF (mismos tonos que ESTADO_RGB; extremo más oscuro).
export const RIESGO_RGB = {
  bajo: [22, 163, 74],
  medio: [217, 119, 6],
  alto: [220, 38, 38],
  extremo: [153, 27, 27],
};

export const TIPOS_EVALUACION = [
  { id: 'anual', label: 'Anual' },
  { id: 'tormenta', label: 'Después de tormenta' },
  { id: 'seguimiento', label: 'Seguimiento' },
  { id: 'otro', label: 'Otra' },
];
export const tipoEvaluacionLabel = (t) => TIPOS_EVALUACION.find((x) => x.id === t)?.label || t || 'Sin dato';

export const esRiesgoAlto = (r) => r === 'alto' || r === 'extremo';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const idArbolValido = (id) => typeof id === 'string' && UUID.test(id);
export const rutaArbol = (id) => `/arboles/${id}`;

// ── Evaluaciones ──
const ordenEval = (a, b) => (b.fecha || '').localeCompare(a.fecha || '')
  || (b.created_at || '').localeCompare(a.created_at || '') || String(b.id).localeCompare(String(a.id));

export const evaluacionesOrdenadas = (evals) => [...(evals || [])].sort(ordenEval);

// Última evaluación NO anulada (la que manda), o null.
export const ultimaEvaluacion = (evals) => evaluacionesOrdenadas((evals || []).filter((e) => e && !e.anulada))[0] || null;

// Estado derivado de un árbol a partir de sus evaluaciones (mismo cálculo que
// la vista, para la ficha y para cuando se tiene la lista de evaluaciones).
export function estadoDesdeEvaluaciones(evals, hoy) {
  const ultima = ultimaEvaluacion(evals);
  const proxima = ultima?.proxima_evaluacion || null;
  // El riesgo sale de la última evaluación que lo CLASIFICÓ (las visitas de
  // seguimiento pueden no clasificarlo), igual que cables_v_arboles_estado.
  const clasificada = ultimaEvaluacion((evals || []).filter((e) => e && e.riesgo));
  return {
    ultima,
    riesgo: clasificada?.riesgo || null,
    riesgo_fecha: clasificada?.fecha || null,
    proxima_evaluacion: proxima,
    sin_evaluacion: !ultima,
    vencida: !!(proxima && hoy && hoy > proxima),
  };
}

// Normaliza una fila de la vista cables_v_arboles_estado (tolera nombres
// alternativos de la última evaluación) a un mismo objeto para la pantalla.
export function normalizarFilaEstado(f, hoy) {
  const riesgo = f.riesgo || f.ultima_riesgo || null;
  const proxima = f.proxima_evaluacion || f.ultima_proxima_evaluacion || null;
  const fechaUltima = f.ultima_fecha || f.ultima_evaluacion_fecha || f.fecha_evaluacion || null;
  const sinEval = typeof f.sin_evaluacion === 'boolean' ? f.sin_evaluacion : !fechaUltima && !riesgo;
  const vencida = typeof f.vencida === 'boolean' ? f.vencida : !!(proxima && hoy && hoy > proxima);
  return {
    ...f,
    instalacion: instalacionDe(f),
    riesgo,
    proxima_evaluacion: proxima,
    ultima_fecha: fechaUltima,
    ultimo_evaluador: f.ultimo_evaluador || f.evaluador_nombre || f.ultima_evaluador_nombre || null,
    sin_evaluacion: sinEval,
    vencida,
    menciones_30d: Number(f.menciones_30d ?? f.menciones ?? f.alertas_30d ?? 0) || 0,
  };
}

// ── Lista ──
export const FILTROS_RIESGO = [
  { id: '', label: 'Todos' },
  ...RIESGOS.map((r) => ({ id: r, label: RIESGO_LABEL[r] })),
  { id: 'vencida', label: 'Vencida' },
  { id: 'sin_evaluar', label: 'Sin evaluar' },
];

// Código natural: A2 antes que A10.
export const compararCodigo = (a, b) => String(a?.codigo || '').localeCompare(String(b?.codigo || ''), 'es', { numeric: true });

export function filtrarArboles(lista, { instalacion, riesgo = '', q = '', incluirBajas = false } = {}) {
  const t = normalizarTexto(q);
  return (lista || []).filter((a) => {
    if (instalacion && instalacionDe(a) !== instalacion) return false;
    if (!incluirBajas && a.activo === false) return false;
    if (riesgo === 'vencida' && !a.vencida) return false;
    if (riesgo === 'sin_evaluar' && !a.sin_evaluacion) return false;
    if (RIESGOS.includes(riesgo) && a.riesgo !== riesgo) return false;
    if (t) {
      const texto = normalizarTexto([a.codigo, a.especie, a.nombre_comun, a.sostiene, a.ubicacion].filter(Boolean).join(' '));
      if (!texto.includes(t)) return false;
    }
    return true;
  }).sort(compararCodigo);
}

export function resumenArboles(lista) {
  const activos = (lista || []).filter((a) => a.activo !== false);
  return {
    activos: activos.length,
    altoExtremo: activos.filter((a) => esRiesgoAlto(a.riesgo)).length,
    extremo: activos.filter((a) => a.riesgo === 'extremo').length,
    vencidas: activos.filter((a) => a.vencida).length,
    sinEvaluacion: activos.filter((a) => a.sin_evaluacion).length,
  };
}

// Cuántos árboles activos caen en cada filtro (los chips de la lista).
// Mismas llaves que FILTROS_RIESGO: '' = todos.
export function conteoFiltros(lista) {
  const activos = (lista || []).filter((a) => a.activo !== false);
  const out = { '': activos.length, vencida: 0, sin_evaluar: 0 };
  for (const r of RIESGOS) out[r] = 0;
  for (const a of activos) {
    if (RIESGOS.includes(a.riesgo)) out[a.riesgo] += 1;
    if (a.vencida) out.vencida += 1;
    if (a.sin_evaluacion) out.sin_evaluar += 1;
  }
  return out;
}

// Punto de estado discreto de las fichas (color + texto, nunca solo color).
export const RIESGO_PUNTO = {
  bajo: 'bg-success',
  medio: 'bg-warning',
  alto: 'bg-error',
  extremo: 'bg-error ring-2 ring-error/30',
};

// ── Ficha ──
const texto = (v) => {
  const s = String(v ?? '').trim();
  return s || null;
};
const numero = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

export const CAMPOS_FICHA = [
  { k: 'codigo', label: 'Código' },
  { k: 'especie', label: 'Especie' },
  { k: 'nombre_comun', label: 'Nombre común' },
  { k: 'instalacion', label: 'Instalación' },
  { k: 'sostiene', label: 'Qué sostiene' },
  { k: 'ubicacion', label: 'Ubicación' },
  { k: 'lat', label: 'Latitud' },
  { k: 'lng', label: 'Longitud' },
  { k: 'dap_cm', label: 'DAP (cm)' },
  { k: 'altura_m', label: 'Altura (m)' },
  { k: 'foto_path', label: 'Foto' },
  { k: 'notas', label: 'Notas' },
  { k: 'activo', label: 'Activo' },
  { k: 'baja_motivo', label: 'Motivo de baja' },
];

// Formulario (texto) a fila para la base. Devuelve { datos, errores }.
export function prepararArbol(form) {
  const errores = {};
  const codigo = texto(String(form.codigo ?? '').replace(/\s+/g, ''))?.toUpperCase() || null;
  if (!codigo) errores.codigo = 'Escriba el código del árbol (por ejemplo A1).';
  else if (codigo.length > 20) errores.codigo = 'Código demasiado largo.';
  const datos = {
    codigo,
    especie: texto(form.especie),
    nombre_comun: texto(form.nombre_comun),
    instalacion: instalacionValida(form.instalacion) ? form.instalacion : 'canopy',
    sostiene: texto(form.sostiene),
    ubicacion: texto(form.ubicacion),
    notas: texto(form.notas),
  };
  const rangos = { lat: [-90, 90], lng: [-180, 180], dap_cm: [0, 2000], altura_m: [0, 150] };
  for (const [k, [min, max]] of Object.entries(rangos)) {
    const n = numero(form[k]);
    if (Number.isNaN(n)) errores[k] = 'Escriba un número.';
    else if (n !== null && (n < min || n > max)) errores[k] = `Debe estar entre ${min} y ${max}.`;
    datos[k] = Number.isNaN(n) ? null : n;
  }
  return { datos, errores };
}

export const arbolAFormulario = (a = {}) => ({
  codigo: a.codigo || '', especie: a.especie || '', nombre_comun: a.nombre_comun || '',
  instalacion: a.instalacion || 'canopy', sostiene: a.sostiene || '', ubicacion: a.ubicacion || '',
  lat: a.lat ?? '', lng: a.lng ?? '', dap_cm: a.dap_cm ?? '', altura_m: a.altura_m ?? '', notas: a.notas || '',
});

// Solo los campos que cambiaron (para no tocar lo que no se editó).
export function cambiosArbol(anterior, datos) {
  const out = {};
  for (const [k, v] of Object.entries(datos)) {
    const a = anterior?.[k] ?? null;
    if (String(a ?? '') !== String(v ?? '')) out[k] = v;
  }
  return out;
}

// ── Evaluación ──
export const evaluacionVacia = (hoy) => ({
  fecha: hoy || '', tipo: 'anual', evaluador_nombre: '', evaluador_profesion: '', evaluador_colegiado: '',
  raices: '', tronco: '', copa: '', plagas_enfermedades: '', inclinacion_grados: '', riesgo: '',
  recomendaciones: '', acciones: '', proxima_evaluacion: '',
});

export function prepararEvaluacion(form, hoy) {
  const errores = {};
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.fecha || '')) errores.fecha = 'Elija la fecha de la evaluación.';
  else if (hoy && form.fecha > hoy) errores.fecha = 'La fecha no puede ser futura.';
  if (!TIPOS_EVALUACION.some((t) => t.id === form.tipo)) errores.tipo = 'Elija el tipo de evaluación.';
  if (!texto(form.evaluador_nombre)) errores.evaluador_nombre = 'Escriba el nombre de quien evaluó.';
  if (!RIESGOS.includes(form.riesgo)) errores.riesgo = 'Elija el nivel de riesgo.';
  const incl = numero(form.inclinacion_grados);
  if (Number.isNaN(incl)) errores.inclinacion_grados = 'Escriba un número.';
  else if (incl !== null && (incl < 0 || incl > 90)) errores.inclinacion_grados = 'Debe estar entre 0 y 90.';
  if (form.proxima_evaluacion) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.proxima_evaluacion)) errores.proxima_evaluacion = 'Fecha no válida.';
    else if (form.fecha && form.proxima_evaluacion <= form.fecha) errores.proxima_evaluacion = 'Debe ser posterior a la evaluación.';
  }
  const datos = {
    fecha: form.fecha,
    tipo: form.tipo,
    evaluador_nombre: texto(form.evaluador_nombre),
    evaluador_profesion: texto(form.evaluador_profesion),
    evaluador_colegiado: texto(form.evaluador_colegiado),
    raices: texto(form.raices),
    tronco: texto(form.tronco),
    copa: texto(form.copa),
    plagas_enfermedades: texto(form.plagas_enfermedades),
    inclinacion_grados: Number.isNaN(incl) ? null : incl,
    riesgo: form.riesgo,
    recomendaciones: texto(form.recomendaciones),
    acciones: texto(form.acciones),
    proxima_evaluacion: form.proxima_evaluacion || null,
  };
  return { datos, errores };
}

// ── Inspección diaria ──
// Ids de árboles que una inspección menciona (detalles.<ítem>.arboles, hoy
// solo estado_fitosanitario). Solo uuid válidos y sin repetir.
export function arbolesDeInspeccion(r) {
  const out = [];
  for (const d of Object.values(r?.detalles || {})) {
    for (const id of Array.isArray(d?.arboles) ? d.arboles : []) {
      if (idArbolValido(id) && !out.includes(id)) out.push(id);
    }
  }
  return out;
}

// Menciones de un árbol en inspecciones (vigentes), más nuevas primero.
export function mencionesDeArbol(inspecciones, arbolId) {
  const out = [];
  for (const r of inspecciones || []) {
    if (!r || r.anulada) continue;
    for (const [key, d] of Object.entries(r.detalles || {})) {
      if (!Array.isArray(d?.arboles) || !d.arboles.includes(arbolId)) continue;
      out.push({
        inspeccion_id: r.id, fecha: r.fecha, guia: r.guia_nombre || '', itemKey: key,
        estado: r.resultados?.[key] || null, nota: d.nota || '',
      });
    }
  }
  return out.sort((a, b) => (b.fecha || '').localeCompare(a.fecha || '') || String(b.inspeccion_id).localeCompare(String(a.inspeccion_id)));
}

// ── Historial de la ficha ──
// Cada fila del historial guarda la ficha ANTERIOR a un cambio. Comparando
// cada versión con la siguiente (la última, con la ficha actual) se obtiene
// qué cambió, quién y cuándo. Tolera nombres de columna alternativos.
const snapshot = (h) => h?.anterior || h?.fila_anterior || h?.datos || h?.old || h?.antes || {};
const cuando = (h) => h?.cambiado_at || h?.created_at || h?.at || null;
const quien = (h) => h?.cambiado_por || h?.created_by || h?.usuario || h?.por || null;

export function historialFicha(historial, actual, campos = CAMPOS_FICHA) {
  const orden = [...(historial || [])].sort((a, b) => String(cuando(a) || '').localeCompare(String(cuando(b) || '')));
  const out = [];
  for (let i = 0; i < orden.length; i++) {
    const antes = snapshot(orden[i]);
    const despues = i + 1 < orden.length ? snapshot(orden[i + 1]) : actual || {};
    const cambios = campos
      .filter(({ k }) => String(antes[k] ?? '') !== String(despues[k] ?? ''))
      .map(({ k, label }) => ({ campo: k, label, antes: antes[k] ?? null, despues: despues[k] ?? null }));
    out.push({ id: orden[i].id ?? i, at: cuando(orden[i]), por: quien(orden[i]), cambios });
  }
  return out.reverse();
}

export const valorCampo = (k, v) => {
  if (v === null || v === undefined || v === '') return 'vacío';
  if (k === 'activo') return v ? 'sí' : 'no';
  if (k === 'instalacion') return etiquetaInstalacion(v);
  if (k === 'foto_path') return 'foto nueva';
  return String(v);
};

export const nombreArbol = (a) => [a?.codigo, a?.nombre_comun || a?.especie].filter(Boolean).join(' · ') || 'Árbol';

// Admin y editor registran árboles, editan fichas y cargan evaluaciones.
export const puedeEditarArboles = (role) => role === 'admin' || role === 'editor';

// ── Reporte ──
// Árboles de una instalación para el reporte (PDF y CSV): estado de la vista
// más las menciones de la inspección diaria DENTRO del período (las
// inspecciones que ya cargó la pantalla). arboles = null si no se pudo
// consultar (el reporte lo dice en vez de omitir la sección).
export function arbolesParaReporte({ arboles, inspecciones, instalacion, hoy }) {
  if (!Array.isArray(arboles)) return null;
  const lista = filtrarArboles(arboles.map((a) => normalizarFilaEstado(a, hoy)), { instalacion });
  return lista.map((a) => {
    const m = mencionesDeArbol(inspecciones, a.id);
    return {
      ...a,
      menciones: m.length,
      mencionesTexto: m.map((x) => `${x.fecha} ${x.estado || ''}`.trim()).join(' / '),
    };
  });
}

// ── Presentación de la ficha ──
// Las notas de la carga inicial guardan varios datos como texto
// "Clave: valor; Clave: valor; texto libre". Se separan los pares conocidos
// para mostrarlos en su lugar; lo que sobra queda como observaciones.
export const PARES_NOTAS = [
  { k: 'nombre_cientifico', clave: 'nombre cientifico', label: 'Nombre científico' },
  { k: 'familia', clave: 'familia', label: 'Familia' },
  { k: 'altura_plataforma', clave: 'altura de la plataforma', label: 'Altura de la plataforma' },
  { k: 'pendiente', clave: 'pendiente del terreno', label: 'Pendiente del terreno' },
  { k: 'crtm05', clave: 'coordenadas crtm05', label: 'Coordenadas CRTM05' },
];

const sinTildes = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

// "13.42m" -> "13.42 m"; el resto se deja como lo escribió el informe.
const espaciarUnidad = (v) => v.replace(/^(\d+(?:[.,]\d+)?)\s*(cm|m|%)$/i, (_, n, u) => (u === '%' ? `${n}%` : `${n} ${u}`));

export function datosDeNotas(notas) {
  const datos = {};
  const resto = [];
  for (const parte of String(notas || '').split(/;\s*/)) {
    const s = parte.trim();
    if (!s) continue;
    const m = s.match(/^([^:]{3,40}):\s*([\s\S]+)$/);
    const par = m && PARES_NOTAS.find((p) => p.clave === sinTildes(m[1]));
    if (par && !(par.k in datos)) datos[par.k] = espaciarUnidad(m[2].trim());
    else resto.push(s);
  }
  const observaciones = resto.join('; ').trim();
  return { datos, observaciones: observaciones || null };
}

// "... Fuente: Informe X." al final de un texto -> { texto, fuente }.
export function separarFuente(valor) {
  // Las marcas "[historial|cables|declaradas AAAA-MM-DD]" solo sirven para revertir
  // una carga, y los identificadores de correo ("msg 19fb...") o rutas locales que
  // dejó la búsqueda no le dicen nada al usuario: no se muestran.
  const s = String(valor ?? '')
    .replace(/\s*\[(?:historial|cables|declaradas) \d{4}-\d{2}-\d{2}\]/g, '')
    .replace(/\s*\((?:[^()]*\b(?:msg|local)\b[^()]*)\)/g, '')
    .replace(/;?\s*\bmsg\s+[0-9a-f]{12,}\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!s) return { texto: null, fuente: null };
  const i = s.search(/(?:^|\s)Fuente:\s/);
  if (i < 0) return { texto: s, fuente: null };
  const texto = s.slice(0, i).trim();
  const fuente = s.slice(i).replace(/^\s*Fuente:\s*/, '').trim().replace(/\.$/, '');
  return { texto: texto || null, fuente: fuente || null };
}

// Evaluaciones (ya ordenadas, más nueva primero) agrupadas por año.
export function agruparPorAnio(evals) {
  const grupos = [];
  for (const e of evals || []) {
    const anio = /^\d{4}/.test(e?.fecha || '') ? e.fecha.slice(0, 4) : 'Sin fecha';
    let g = grupos[grupos.length - 1];
    if (!g || g.anio !== anio) { g = { anio, evaluaciones: [] }; grupos.push(g); }
    g.evaluaciones.push(e);
  }
  return grupos;
}

// Años que arrancan abiertos: el actual y los que contienen las n más recientes.
export function aniosAbiertos(grupos, hoy, n = 3) {
  const abiertos = new Set();
  if (hoy) abiertos.add(hoy.slice(0, 4));
  let vistos = 0;
  for (const g of grupos || []) {
    if (vistos >= n) break;
    abiertos.add(g.anio);
    vistos += g.evaluaciones.length;
  }
  return abiertos;
}

// Riesgo más alto de un grupo (las anuladas no cuentan).
export const riesgoMaximo = (evals) => (evals || [])
  .filter((e) => e && !e.anulada && RIESGO_ORDEN[e.riesgo])
  .reduce((max, e) => (!max || RIESGO_ORDEN[e.riesgo] > RIESGO_ORDEN[max] ? e.riesgo : max), null);

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'set', 'oct', 'nov', 'dic'];
// "2026-02-16" -> "16 feb" (el año va en el encabezado del grupo).
export const diaMes = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || '')
  ? `${Number(iso.slice(8, 10))} ${MESES[Number(iso.slice(5, 7)) - 1]}` : '');

// Medida numérica con unidad, o null si no hay dato.
export const medida = (v, unidad) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? `${n.toLocaleString('es-CR', { maximumFractionDigits: 2 })} ${unidad}` : null;
};

// Plazo en palabras entre hoy y una fecha: "en 12 días", "hoy", "hace 3 días".
export function plazo(hoy, fecha) {
  if (!/^\d{4}-\d{2}-\d{2}/.test(hoy || '') || !/^\d{4}-\d{2}-\d{2}/.test(fecha || '')) return '';
  const d = Math.round((Date.parse(`${fecha.slice(0, 10)}T12:00:00Z`) - Date.parse(`${hoy.slice(0, 10)}T12:00:00Z`)) / 86400000);
  if (d === 0) return 'hoy';
  if (d === 1) return 'mañana';
  if (d === -1) return 'ayer';
  return d > 0 ? `en ${d} días` : `hace ${-d} días`;
}
